import { ApolloClient } from '@apollo/client';
import { GetServerSideProps, NextPage } from 'next';
import { SSRConfig } from 'next-i18next/pages';
import { useEffect } from 'react';

import { JobPositionDocument, JobPositionQuery } from '@pflegia/data-access/multiposting.generated';
import { UTM_REENTRY_SOURCE_COOKIE_KEY } from '@pflegia/types/common/constants/utm-keys';
import { UtmReentrySource } from '@pflegia/types/common/enums';
import { useAuth } from '@pflegia/ui/contexts/auth.context';
import { cookieStorage } from '@pflegia/ui/storage/cookie-storage';
import { isValidUuidV4 } from '@pflegia/util/common/is-valid-uuid-v4';
import { assertIsDefined } from '@pflegia/util/functions/type-guards';

import MultipostingFunnel from '@funnel/components/multiposting/MultipostingFunnel';

import { getApolloClientSsr } from '@shared/application/apollo-ssr-client';
import NextSeo from '@shared/components/NextSeo';
import { LOCALES } from '@shared/constants/locale';
import { LOGGED_CANDIDATE_REQUIRED_NAMESPACES } from '@shared/constants/required-namespaces';
import { getApolloCacheForHydration } from '@shared/graphql/apollo-hydration';
import { isAuthSsrError } from '@shared/utils/is-auth-ssr-error';
import { serverSideTranslations } from '@shared/utils/server-side-translations';

import { getGlobalSsrProps } from '@utils/global-ssr-props';

interface MultipostingProps {
  employerName: string;
  jobPositionTitle: string;
  jobTitles: JobPositionQuery['jobPosition']['jobTitles'];
}

const Multiposting: NextPage<MultipostingProps> = ({ employerName, jobPositionTitle }) => {
  const authenticated = useAuth();

  useEffect(() => {
    const currentReentryValue = cookieStorage.getItem(UTM_REENTRY_SOURCE_COOKIE_KEY);
    if (!authenticated && !currentReentryValue) cookieStorage.setItem(UTM_REENTRY_SOURCE_COOKIE_KEY, UtmReentrySource.Multiposting);
  }, [authenticated]);

  return (
    <>
      <NextSeo noindex title={`${jobPositionTitle} - ${employerName}`} />
      <MultipostingFunnel />
    </>
  );
};

export const getServerSideProps: GetServerSideProps<MultipostingProps & SSRConfig, { jobId: string }> = async ({
  locale = LOCALES[0],
  params,
  query,
  req,
}) => {
  if (!params?.jobId) return { notFound: true };

  const { jobId } = params;
  if (!isValidUuidV4(jobId)) return { notFound: true };

  const { external } = query;
  const isExternal = external === 'true';
  const client = getApolloClientSsr(req, locale);
  const jobPosition = await getJobPosition(client, jobId, isExternal);
  if (!jobPosition) return { notFound: true };

  const { employer, jobTitles, title: jobPositionTitle } = jobPosition;
  assertIsDefined(employer);
  const { name: employerName } = employer;
  assertIsDefined(employerName);
  assertIsDefined(jobTitles);
  assertIsDefined(jobPositionTitle);
  if (!jobTitles.length) return { notFound: true };

  const translations = await serverSideTranslations(locale, [...LOGGED_CANDIDATE_REQUIRED_NAMESPACES, 'jobTitles']);

  return {
    props: {
      ...getApolloCacheForHydration(client),
      ...getGlobalSsrProps(req),
      ...translations,
      employerName,
      jobPositionTitle,
      jobTitles,
    },
  };
};

const getJobPosition = async (client: ApolloClient, id: string, external = false) => {
  try {
    const result = await client.query<JobPositionQuery>({
      query: JobPositionDocument,
      variables: { id, external },
    });
    return result.data?.jobPosition;
  } catch (error) {
    if (error instanceof Error && (error.message === 'Job position not found' || isAuthSsrError(error))) {
      return null;
    }

    // eslint-disable-next-line no-console
    console.error(error);
    return null;
  }
};

export default Multiposting;
